import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Photo from "./Photo";
import { Title, Kicker } from "./bits";
import { T, GALLERY } from "./content";

export default function Galerie({ lang }) {
  const t = T[lang];
  const [filter, setFilter] = useState("all");
  const [index, setIndex] = useState(-1);

  const items = GALLERY.filter((g) => filter === "all" || g.cat === filter);
  const open = index >= 0 && index < items.length;
  const current = open ? items[index] : null;

  useEffect(() => {
    setIndex(-1);
  }, [filter]);

  const close = useCallback(() => setIndex(-1), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + items.length) % items.length), [items.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [open, close, prev, next]);

  const cap = (g) => (lang === "fr" ? g.noteFr : g.noteEn);
  const title = (g) => (lang === "fr" ? g.titleFr : g.titleEn);

  return (
    <section className="section" id="galerie" data-testid="section-galerie">
      <div className="container">
        <div className="section-head reveal">
          <Kicker withDash>{t.galerieKicker}</Kicker>
          <Title parts={t.galerieTitle} tag="h2" />
        </div>

        <div className="filters reveal" role="tablist">
          {t.filters.map((f) => (
            <button
              key={f.key}
              className={`filter ${filter === f.key ? "active" : ""}`}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="gallery-grid reveal">
          {items.map((g, i) => (
            <div
              key={g.id}
              className="tile"
              tabIndex={0}
              role="button"
              aria-label={title(g)}
              data-testid={`tile-${g.id}`}
              onClick={() => setIndex(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIndex(i);
                }
              }}
            >
              <div className="tile__media">
                <Photo slug={g.slug} label={title(g)} className="photo-img" />
              </div>
              <div className="tile__cap">{cap(g)}</div>
            </div>
          ))}
        </div>
      </div>

      {open &&
        createPortal(
          <div className="lightbox" data-testid="lightbox" onClick={close}>
            <button className="lightbox__close" onClick={close} aria-label="Close" data-testid="lightbox-close">
              <X size={20} />
            </button>
            <button
              className="lightbox__nav prev"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={22} />
            </button>
            <div className="lightbox__stage" onClick={(e) => e.stopPropagation()}>
              <Photo slug={current.slug} label={title(current)} className="photo-img" />
              <span className="lightbox__cap">{cap(current)}</span>
            </div>
            <button
              className="lightbox__nav next"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next"
              data-testid="lightbox-next"
            >
              <ChevronRight size={22} />
            </button>
          </div>,
          document.body
        )}
    </section>
  );
}
